import { Box, Button, Center, Combobox, Group, SimpleGrid, useMatches } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { CarCard } from "../CarCard";
import { CarComparator } from "../CarComparator";
import { CarTableRow } from "../CarTableRow";
import { CarsTableFilters, CarsTableFiltersPayload } from "./components";
import CarsTableEmptyState from "./components/CarsTableEmptyState/CarsTableEmptyState";
import CompareButton from "./components/CompareButton/CompareButton";
import ViewSwitcher from "./components/ViewSwitcher/ViewSwitcher";

import {
  Collapsable,
  EmptyStateRevamped,
  ListTable,
  Modal,
  useColumnSorter,
} from "@/components/elements";
import PopoverTable from "@/components/elements/PopoverTable/PopoverTable";
import { IconFilter, IconFilterFilled, IconLock, IconTags } from "@/components/icons";
import { KPI_COLUMNS_BASE, KPI_COLUMNS_FREE } from "@/config/plans.config";
import { useGarages } from "@/services/garages/get-garages";
import { usePlansUser } from "@/services/plans/get-user-plan";

import type {
  ListTableColumnSortBy,
  ListTableComparisonProps,
  ListTableTh,
} from "@/components/elements";
import type { Car, CarColumns } from "@/types/car";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

type CarsView = "cards" | "table";

export type CarsTableProps = {
  cars?: Car[];
  columns?: CarColumns[];
  columnsDisabled?: CarColumns[];
  conditions?: Car["condition"][];
  defaultView?: CarsView;
  getPath?: (carId: string) => string;
  namespace?: string;
  onAddCar?: () => void;
  onCarsCompare?: (car1Id: string, car2Id: string, car3Id?: string) => void;
  onConditionChange?: (condition?: Car["condition"]) => void;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSearch?: (query: string) => void;
  onStatusChange?: (status?: Car["status"]) => void;
  page?: number;
  pageSize?: number;
  statuses?: Car["status"][];
  totalCars?: number;
  totalCarsUnfiltered?: number;
  garageId?: string;
  canAccess?: boolean;
} & MantineStyleProps;

export const CarsTable: FC<CarsTableProps> = ({
  cars = [],
  columns = [],
  columnsDisabled,
  conditions = [],
  defaultView = "table",
  getPath,
  namespace = "garage-table",
  onAddCar,
  onCarsCompare,
  onConditionChange,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onStatusChange,
  page = 1,
  pageSize = 25,
  statuses = [],
  totalCars,
  totalCarsUnfiltered,
  garageId,
  canAccess = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [carsView, setCarsView] = useState(defaultView);
  const [status, setStatus] = useState<Car["status"]>();
  const [condition, setCondition] = useState<Car["condition"]>();
  const [opened, { open, close }] = useDisclosure();
  const isMobile = useMatches({ base: true, md: false });
  const hasComparison = !!onCarsCompare;
  const { getSortById, getUpdateSort } = useColumnSorter<Car, CarColumns>(`${namespace}-sort`);

  const { data: plans } = usePlansUser();
  const userPlan = plans?.name;
  const canCompareByPlan = userPlan === "collector" || userPlan === "professional";
  const { data: garages } = useGarages();
  const totalGarageCars = (garages?.results ?? []).reduce(
    (total, garage) => total + garage.data.num_cars,
    0
  );

  const [popoverOpened, setPopoverOpened] = useState(false);

  const tableColumns: ListTableTh[] = columns.map((id) => ({
    filter: getColumnFilter(id),
    id,
    label: t(`car.${id}`),
    sorter: getColumnSorter(id),
  }));
  // If the user is on the starter plan, also lock the "condition" column
  const KPI_COLUMNS = [
    ...(userPlan === "starter" ? KPI_COLUMNS_FREE : KPI_COLUMNS_BASE),
  ] as CarColumns[];

  const orderedColumnIds = canAccess
    ? columns
    : [
        ...columns.filter((c) => !KPI_COLUMNS.includes(c as CarColumns)),
        ...columns.filter((c) => KPI_COLUMNS.includes(c as CarColumns)),
      ];

  const orderedTableColumns: ListTableTh[] = orderedColumnIds.map((id) => {
    const isLocked = !canAccess && KPI_COLUMNS.includes(id as CarColumns);
    return {
      filter: getColumnFilter(id as CarColumns),
      id,
      label: t(`car.${id}`),
      ...(isLocked
        ? { icon: <IconLock size={22} style={{ flexShrink: 0, opacity: 0.6 }} /> }
        : { sorter: getColumnSorter(id as CarColumns) }),
    };
  });

  const total = typeof totalCars === "number" ? totalCars : cars.length;
  const unfilteredTotal = typeof totalCarsUnfiltered === "number" ? totalCarsUnfiltered : total;

  // Preserve the initial unfiltered total so UI controls (like the search input)
  // remain visible even when a search returns 0 results. This value is captured
  // once from the first available total (unfiltered/totalCars/cars.length).
  const initialUnfilteredRef = useRef<number | null>(null);
  const initialGarageIdRef = useRef<string | undefined>(garageId);

  useEffect(() => {
    // Reset the initial unfiltered total when switching garage.
    // Each garage must have its own empty state.
    if (initialGarageIdRef.current !== garageId) {
      initialGarageIdRef.current = garageId;
      initialUnfilteredRef.current = null;
    }

    // Capture an initial unfiltered total once, but allow updating from 0 -> >0
    // so controls (search/filter/etc.) reappear when the first asset is added.
    if (initialUnfilteredRef.current === null || initialUnfilteredRef.current === 0) {
      const initial =
        typeof totalCarsUnfiltered === "number" && totalCarsUnfiltered > 0
          ? totalCarsUnfiltered
          : typeof totalCars === "number" && totalCars > 0
            ? totalCars
            : cars.length > 0
              ? cars.length
              : null;

      if (typeof initial === "number") {
        initialUnfilteredRef.current = initial;
      }
    }
  }, [garageId, totalCarsUnfiltered, totalCars, cars.length]);

  const hasPageSizeChanger = !!onPageSizeChange && total > 10;
  const hasPagination = !!onPageChange && total > pageSize;
  const showControls =
    initialUnfilteredRef.current === null
      ? typeof totalCarsUnfiltered === "number" && totalCarsUnfiltered > 0
        ? true
        : typeof totalCars === "number" && totalCars > 0
          ? true
          : cars.length > 0
      : initialUnfilteredRef.current > 0;

  const hasUnfilteredCars = unfilteredTotal > 0;
  const isEmptyState = cars.length === 0 && !hasUnfilteredCars;

  function getColumnSorter(id: CarColumns) {
    return (
      <ListTable.ColumnSorter
        tooltip={t("garage.table_sort_tooltip")}
        sortBy={getSortById(id)}
        onSortChange={onChangeSortBy(id)}
      />
    );
  }

  function getColumnFilter(id: CarColumns) {
    switch (id) {
      case "status":
        return statuses.length > 0 ? (
          <ListTable.ColumnFilter
            filtered={!!status}
            label={t("car.status_filter")}
            tooltip={t("car.status_filter")}
          >
            <Combobox
              onOptionSubmit={(value) =>
                handleStatusChange(status === value ? undefined : (value as Car["status"]))
              }
            >
              <Combobox.Options>
                {statuses.map((value) => (
                  <Combobox.Option key={value} value={value} active={value === status}>
                    {t(`car.statuses.${value}`, value)}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            </Combobox>
          </ListTable.ColumnFilter>
        ) : undefined;
      case "condition":
        return conditions.length > 0 ? (
          <ListTable.ColumnFilter
            filtered={!!condition}
            label={t("car.condition_filter")}
            tooltip={t("car.condition_filter")}
          >
            <Combobox
              onOptionSubmit={(value) =>
                handleConditionChange(condition === value ? undefined : (value as Car["condition"]))
              }
            >
              <Combobox.Options>
                {conditions.map((value) => (
                  <Combobox.Option key={value} value={value} active={value === condition}>
                    {t(`car.conditions.${value}`, value)}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            </Combobox>
          </ListTable.ColumnFilter>
        ) : undefined;
      default:
        return undefined;
    }
  }

  function handleCarsCompare(car1Id: string, car2Id: string, car3Id?: string) {
    onCarsCompare?.(car1Id, car2Id, car3Id);
    close();
  }

  function handleConditionChange(condition?: Car["condition"]) {
    setCondition(condition);
    onPageChange?.(1);
    onConditionChange?.(condition);
  }

  function handleFiltersChange({ condition, status }: CarsTableFiltersPayload) {
    handleConditionChange(condition);
    handleStatusChange(status);
  }

  function handleStatusChange(status?: Car["status"]) {
    setStatus(status);
    onPageChange?.(1);
    onStatusChange?.(status);
  }

  function handleSearch(query: string) {
    onPageChange?.(1);
    onSearch?.(query);
  }

  function handlePageSizeChange(pageSize: number) {
    onPageChange?.(1);
    onPageSizeChange?.(pageSize);
  }

  function onChangeSortBy(id: CarColumns) {
    return (sortBy?: ListTableColumnSortBy) => {
      getUpdateSort(id)(sortBy);
    };
  }

  return (
    <>
      {/* pass disabled columns so they can't be toggled/reordered; KPI columns are disabled when canAccess is false */}
      <ListTable.Context
        columns={canAccess ? tableColumns : orderedTableColumns}
        disabled={
          columnsDisabled
            ? canAccess
              ? columnsDisabled
              : [...columnsDisabled, ...KPI_COLUMNS]
            : canAccess
              ? []
              : KPI_COLUMNS
        }
        namespace={namespace}
      >
        {({ filteredColumns, headingColumns, onFilteredColumnsChange }) => {
          const columnsId = headingColumns.map(({ id }) => id) as CarColumns[];

          return (
            <ListTable.SelectionContext data={cars.map((car) => car.identifier)} maxSelection={3}>
              {({ checkStatus, getRowCheckbox, isSelected, onToggleAll, reset, selected }) => {
                const comparisonProps = hasComparison
                  ? ({
                      checkStatus,
                      hasComparison: true,
                      onToggleAll,
                      withSelection: false,
                    } as ListTableComparisonProps)
                  : {};

                return (
                  <>
                    <Collapsable>
                      {(showControls || isEmptyState) && (
                        <Box
                          opacity={isEmptyState ? 0.6 : 1}
                          style={{
                            filter: isEmptyState ? "grayscale(1)" : undefined,
                          }}
                        >
                          <Group
                            gap={isMobile ? "xs" : "md"}
                            p="lg"
                            style={{
                              pointerEvents: isEmptyState ? "none" : undefined,
                              userSelect: isEmptyState ? "none" : undefined,
                            }}
                          >
                            {onSearch && (
                              <ListTable.SearchInput
                                onChange={handleSearch}
                                placeholder={t("garage.search_placeholder")}
                                resetTooltip={t("garage.search_reset_tooltip")}
                                updateOnChange
                              />
                            )}
                            {statuses.length > 0 && (
                              <Collapsable.ToggleButton px="xs">
                                {status ? <IconFilterFilled /> : <IconFilter />}
                              </Collapsable.ToggleButton>
                            )}
                            {isMobile ? (
                              <PopoverTable
                                popoverOpened={popoverOpened}
                                setPopoverOpened={() => setPopoverOpened((prev) => !prev)}
                                actionInPending={false}
                                filteredColumns={filteredColumns}
                                onFilteredColumnsChange={onFilteredColumnsChange}
                              />
                            ) : (
                              <ListTable.ColumnsOrganizer
                                columns={filteredColumns}
                                label={t("common.customizeColumns")}
                                onChange={onFilteredColumnsChange}
                                tooltip={t("common.organizeColumns")}
                              />
                            )}
                            <ViewSwitcher carsView={carsView} setCarsView={setCarsView} />

                            <CompareButton
                              canCompareByPlan={canCompareByPlan}
                              open={open}
                              totalCars={totalGarageCars}
                            />
                          </Group>
                        </Box>
                      )}
                      <Collapsable.Collapse>
                        <CarsTableFilters
                          condition={condition}
                          conditions={conditions}
                          onCancel={() =>
                            handleFiltersChange({ condition: undefined, status: undefined })
                          }
                          onSubmit={handleFiltersChange}
                          status={status}
                          statuses={statuses}
                        />
                      </Collapsable.Collapse>
                    </Collapsable>

                    {carsView === "table" &&
                      (cars.length > 0 ? (
                        <ListTable
                          data={{
                            head: headingColumns,
                            body: cars.map((car) => (
                              <CarTableRow
                                key={car.identifier}
                                checkbox={getRowCheckbox(car.identifier)}
                                columns={columnsId}
                                data={car}
                                canAccess={canAccess}
                                onClick={
                                  getPath ? () => navigate(getPath(car.identifier)) : undefined
                                }
                                selected={isSelected(car.identifier)}
                                to={getPath?.(car.identifier)}
                              />
                            )),
                          }}
                          tableProps={{ highlightOnHover: true }}
                          {...comparisonProps}
                        />
                      ) : !hasUnfilteredCars ? (
                        <CarsTableEmptyState onAddCar={onAddCar} />
                      ) : (
                        /* Empty state when there are no results from the search or applied filters */
                        <Center h="18em">
                          <EmptyStateRevamped />
                        </Center>
                      ))}

                    {carsView === "cards" &&
                      (cars.length > 0 ? (
                        <SimpleGrid cols={{ base: 2, xs: 3, sm: 3, md: 4 }} spacing="lg" p="lg">
                          {cars.map((car) => (
                            <CarCard
                              key={car.identifier}
                              badge={car.nickname}
                              make={car.make}
                              name={car.model}
                              onClick={
                                getPath ? () => navigate(getPath(car.identifier)) : undefined
                              }
                              carId={car.identifier}
                              marketCarId={car.taxonomy_id}
                            />
                          ))}
                        </SimpleGrid>
                      ) : !hasUnfilteredCars ? (
                        <Center h="20em">
                          <EmptyStateRevamped>
                            {onAddCar && (
                              <Button
                                variant="filled"
                                mt="sm"
                                onClick={onAddCar}
                                aria-label={t("dashboard.cars.create_button")}
                              >
                                {t("dashboard.cars.create_button")}
                              </Button>
                            )}
                          </EmptyStateRevamped>
                        </Center>
                      ) : (
                        /* This is smaller because the other <EmptyStateRevamped> includes a button */
                        <Center h="18em">
                          <EmptyStateRevamped />
                        </Center>
                      ))}

                    {cars.length > 0 && (hasPageSizeChanger || hasPagination) && (
                      <Group justify="flex-end" p="lg">
                        {hasPageSizeChanger && (
                          <ListTable.PageSizeChanger
                            label={t("components.ListTable.page_size_changer_label")}
                            onChange={handlePageSizeChange}
                            pageSize={pageSize}
                          />
                        )}
                        {hasPagination && (
                          <ListTable.Pagination
                            divider={t("components.ListTable.pagination_divider")}
                            page={page}
                            pageSize={pageSize}
                            onChange={onPageChange}
                            total={total}
                          />
                        )}
                      </Group>
                    )}

                    {hasComparison && (
                      <Modal
                        opened={opened}
                        onClose={close}
                        centered
                        icon={IconTags}
                        title={t("garage.compare_modal")}
                      >
                        <CarComparator
                          defaultCar1Id={selected[0]}
                          defaultCar2Id={selected[1]}
                          defaultCar3Id={selected[2]}
                          onCancel={close}
                          onCompare={(car1Id, car2Id, car3Id) => {
                            handleCarsCompare(car1Id, car2Id, car3Id);
                            reset();
                          }}
                          garageId={garageId}
                          carsData={cars.map((car) => ({
                            label: `${car.make} ${car.model}${car.nickname ? ` (${car.nickname})` : ""}`,
                            value: car.identifier,
                          }))}
                        />
                      </Modal>
                    )}
                  </>
                );
              }}
            </ListTable.SelectionContext>
          );
        }}
      </ListTable.Context>
    </>
  );
};
